import { isNull, isString } from "@/app/utils"

interface MetricProps {
  name: string
  short_name: string
  text: string
  applyTo: "fund" | "firm" | "both"
  supportsDaily: boolean
  isTermDurationRelevant: boolean
}

// common metric names   - aum, prevAum, aumChangePcent, cumeNetflow, mktSharePcent, netflow, organicGrowthPcent
// fundOnly metric names - expenseRatioPcent, returnPcent, returnPtile
// firmOnly metric names - avgAum, numFunds
export class Metric {
  readonly name: string
  readonly short_name: string
  readonly text: string
  readonly fundOnly: boolean
  readonly firmOnly: boolean
  readonly supportsDaily: boolean
  readonly isTermDurationRelevant: boolean

  constructor({
    name,
    short_name,
    text,
    applyTo,
    supportsDaily,
    isTermDurationRelevant,
  }: MetricProps) {
    const VALID_APPLY_TO = ["fund", "firm", "both"]
    this.name = name
    this.short_name = short_name
    this.text = text
    if (!VALID_APPLY_TO.includes(applyTo))
      throw new Error(
        `Invalid applyTo for metric ${name}. Expected:${VALID_APPLY_TO}, Got:${applyTo}`
      )
    this.fundOnly = applyTo == "fund"
    this.firmOnly = applyTo == "firm"
    this.supportsDaily = supportsDaily
    this.isTermDurationRelevant = isTermDurationRelevant
  }
}
export namespace Metric {
  // define constants for known metric names (from API)
  export const AUM = "aum"
  export const PrevAUM = "prevAum"
  export const AumChange = "aumChangePcent"
  export const Netflow = "netflow"
  export const CumeNetflow = "cumeNetflow"
  export const MktShare = "mktSharePcent"
  export const OrgGrowth = "organicGrowthPcent"
  export const ExpenseRatio = "expenseRatioPcent"
  export const TotalReturn = "returnPcent"
  export const ReturnPtile = "returnPtile"
  export const AvgAUM = "avgAum"
  export const NumFunds = "numFunds"

  export function getMetric(metricName: string): Readonly<Metric> {
    if (isNotValid(metricName))
      throw new Error(
        `Invalid metricName:${metricName}, expected:${_all_metrics_names}`
      )
    const metric = ALL_METRICS.find((m) => m.name == metricName)
    if (metric) return Object.freeze(metric)
    // should not happen
    throw new Error(`Missing valid metricName:${metricName} in ALL_METRICS`)
  }

  export function isValid(metricName: string): boolean {
    return isString(metricName) && _all_metrics_names.includes(metricName)
  }

  export function isNotValid(metricName: string): boolean {
    return !isValid(metricName)
  }

  export function equals(
    metric1: Metric | string,
    metric2: Metric | string
  ): boolean {
    if (isNull(metric1) || isNull(metric2)) return false
    const m1 =
      isString(metric1) ?
        Metric.getMetric(metric1 as string)
      : (metric1 as Metric)
    const m2 =
      isString(metric2) ?
        Metric.getMetric(metric2 as string)
      : (metric2 as Metric)
    if (isNull(m1) || isNull(m2)) return false
    return m1.name == m2.name
  }

  export const ALL_METRICS: Metric[] = [
    // metrics for both funds / firms
    new Metric({
      name: AUM,
      short_name: "AUM",
      text: "AUM",
      applyTo: "both",
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Metric({
      name: PrevAUM,
      short_name: "Prev AUM",
      text: "Prev AUM",
      applyTo: "both",
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Metric({
      name: AumChange,
      short_name: "AUM Δ",
      text: "AUM Change",
      applyTo: "both",
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Metric({
      name: Netflow,
      short_name: "NNF",
      text: "Net Flows",
      applyTo: "both",
      supportsDaily: false,
      isTermDurationRelevant: true,
    }),
    new Metric({
      name: CumeNetflow,
      short_name: "Total NNF",
      text: "Cumulative Flows",
      applyTo: "both",
      supportsDaily: false,
      isTermDurationRelevant: true,
    }),
    new Metric({
      name: MktShare,
      short_name: "Mkt Share",
      text: "Mkt Share",
      applyTo: "both",
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Metric({
      name: OrgGrowth,
      short_name: "NNF/AUM",
      text: "Organic Growth",
      applyTo: "both",
      supportsDaily: false,
      isTermDurationRelevant: true,
    }),
    // metrics for only funds
    new Metric({
      name: ExpenseRatio,
      short_name: "Exp Ratio",
      text: "Expense Ratio",
      applyTo: "fund",
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Metric({
      name: TotalReturn,
      short_name: "TR",
      text: "Total Return",
      applyTo: "fund",
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Metric({
      name: ReturnPtile,
      short_name: "TR %tile",
      text: "Return %tile",
      applyTo: "fund",
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    // metrics for only firms
    new Metric({
      name: AvgAUM,
      short_name: "Avg AUM",
      text: "Avg AUM per Fund",
      applyTo: "firm",
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Metric({
      name: NumFunds,
      short_name: "Fund Count",
      text: "Number of Funds",
      applyTo: "firm",
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
  ]

  export const _all_metrics_names = ALL_METRICS.map((m) => m.name)

  export const _all_metrics_texts = new Map(
    ALL_METRICS.map((m) => [m.name, m.text])
  )

  export const FUND_METRICS = ALL_METRICS.filter((m) => m.fundOnly)

  export const FIRM_METRICS = ALL_METRICS.filter((m) => m.firmOnly)
}
